import { LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import { themeSettings } from "../store/extended-offer-store";
import { publishKlaviyoIdentify } from "../store/cart-store";

/**
 * Bootstrap component that populates theme settings into the extended-offer-store.
 * Should be placed in layout/theme.liquid with data attributes from Liquid.
 *
 * Usage:
 * <theme-settings-provider
 *   data-enable-back-in-stock-alerts="true"
 *   data-klaviyo-company-id="ABC123"
 *   data-shop-domain="example.myshopify.com"
 *   data-show-newsletter-checkbox="true"
 * ></theme-settings-provider>
 */
@customElement("theme-settings-provider")
export class ThemeSettingsProvider extends LitElement {
  @property({ type: String, attribute: "data-enable-back-in-stock-alerts" })
  enableBackInStockAlerts: string | boolean = false;

  @property({ type: String, attribute: "data-klaviyo-company-id" })
  klaviyoCompanyId = "";

  @property({ type: String, attribute: "data-shop-domain" })
  shopDomain = "";

  @property({ type: String, attribute: "data-show-newsletter-checkbox" })
  showNewsletterCheckbox: string | boolean = false;

  @property({ type: String, attribute: "data-promo-codes" })
  promoCodes = "";

  @property({ type: String, attribute: "data-enable-promo-code-modal" })
  enablePromoCodeModal: string | boolean = false;

  @property({ type: String, attribute: "data-customer-email" })
  customerEmail = "";

  constructor() {
    super();
    console.log("[theme-settings-provider] Constructor called");
  }

  connectedCallback() {
    super.connectedCallback();
    console.log("[theme-settings-provider] connectedCallback - attributes:", {
      enableBackInStockAlerts: this.enableBackInStockAlerts,
      klaviyoCompanyId: this.klaviyoCompanyId,
      shopDomain: this.shopDomain,
      showNewsletterCheckbox: this.showNewsletterCheckbox,
    });
  }

  // This runs after properties are updated from attributes
  firstUpdated() {
    console.log("[theme-settings-provider] firstUpdated - populating settings");
    this._populateSettings();
  }

  private _populateSettings() {
    const settings = {
      enableBackInStockAlerts:
        this.enableBackInStockAlerts === true ||
        this.enableBackInStockAlerts === "true",
      klaviyoCompanyId: this.klaviyoCompanyId,
      shopDomain: this.shopDomain,
      showNewsletterCheckbox:
        this.showNewsletterCheckbox === true ||
        this.showNewsletterCheckbox === "true",
      promoCodes: this.promoCodes
        .split(",")
        .map((code) => code.trim().toUpperCase())
        .filter((code) => code.length > 0),
      enablePromoCodeModal:
        this.enablePromoCodeModal === true ||
        this.enablePromoCodeModal === "true",
    };

    console.log("[theme-settings-provider] Populating settings:", settings);
    themeSettings.value = settings;

    // Identify the logged-in customer to Klaviyo early so onsite `track` events
    // (e.g. "Added to Cart") are attributed to their profile. Empty when logged out.
    if (this.customerEmail) publishKlaviyoIdentify(this.customerEmail);
  }

  // No rendering needed - this is a bootstrap component
  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }
}
